import styles from "./ConfirmShipmentsPage.module.css";
import { PlacementShipmentOptionDTO, ShipmentDestination, TransportType } from "@boxem/core-api-schema";
import { amazonAddressToStringSingleLine, toAmazonAddress } from "../../utils";
import { FbaBatchItem, FbaBox, useShipmentContext } from "@/contexts/ShipmentContext";
import { useUserContext } from "@/contexts/UserContext";
import Button from "@/components/forms/Button/Button";
import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import Box from "../ShipmentBox";
import ProductView from "../ProductView";

type ShipmentProps = {
  shipmentOption: PlacementShipmentOptionDTO;
};

type Item = { batchItem: FbaBatchItem, quantity: number };

export default function ShipmentOption({shipmentOption}: ShipmentProps) {
  const { addressBook } = useUserContext();
  const { currentBatch } = useShipmentContext();

  const [viewContentsModalOpen, setViewContentsModalOpen] = useState<boolean>(false);
  const [boxes, setBoxes] = useState<FbaBox[]>(null);
  const [items, setItems] = useState<Item[]>(null);
  const [totalSkus, setTotalSkus] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalWeight, setTotalWeight] = useState(null);

  const formatDestination = (destination: ShipmentDestination): string => {
    return `${destination.warehouseId}, ${amazonAddressToStringSingleLine(destination.address)}`;
  }

  const fromAddress = addressBook.find(a => a.id === currentBatch?.fromId);
  const spd = currentBatch.shipmentType === TransportType.SPD;
  const ltl = !spd;

  useEffect(() => {
    const skus = new Set<string>();
    let totalUnits = 0;
    if (ltl) {
      // in LTL we don't have boxes yet, but we do have items.
      const items = shipmentOption.items?.map(item => {
        const batchItem = currentBatch.items.find(i => i.sku === item.msku);
        skus.add(item.msku);
        totalUnits += item.quantity;
        return { batchItem, quantity: item.quantity };
      });
      setItems(items);
    } else {
      const matched = new Set();
      const boxes = [];
      let totalWeight = 0;
      shipmentOption.boxPackageIds.forEach(packageId => {
        const matchedIndex = currentBatch.boxes.findIndex(
          (b, index) => b.packageId === packageId && !matched.has(index)
        );

        if (matchedIndex < 0) return; // can happen if you abort/get errors moving from step 2 -> 3
        const box = currentBatch.boxes[matchedIndex];
        matched.add(matchedIndex);

        totalWeight += box.weight; // @NOTE assuming lbs...
        box.packingItems.forEach(item => {
          skus.add(item.sku);
          totalUnits += item.quantity;
        });
        boxes.push(box);
      });
      setBoxes(boxes);
      setTotalWeight(totalWeight);
    }
    setTotalSkus(skus.size);
    setTotalUnits(totalUnits);
  }, [currentBatch, shipmentOption]);

  return (
    <>
      <Modal
        className={styles.viewContentsModal}
        open={viewContentsModalOpen}
        onOpenChange={open => setViewContentsModalOpen(open)}
      >
        <ModalHeader><h3>{ltl ? "Items within shipment" : "Boxes within shipment"}</h3></ModalHeader>
        <ModalBody>
          <div className={styles.shipmentContents}>
            {ltl
              ? items?.map((item, i) => <ProductView key={i} item={item.batchItem} quantity={item.quantity} />)
              : boxes?.map((b, i) => <Box key={i} box={b} />)
            }
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="secondary" block onClick={() => setViewContentsModalOpen(false)}>Done</Button>
        </ModalFooter>
      </Modal>

      <div className={`${styles.grid} ${styles.sectionContent} ${styles.shipmentAddresses}`}>
        <span className="weak">Ship from:</span>
        <span className="strong">{fromAddress.name} {amazonAddressToStringSingleLine(toAmazonAddress(fromAddress))}</span>
        <span className="weak">Ship to:</span>
        <span className="strong">{formatDestination(shipmentOption.fulfillmentCenter)}</span>
      </div>
      <div className={`${styles.sectionContent} ${styles.row}`}>
        <div className={styles.shipmentDetails}>
          {spd && <span className="weak">Boxes: <span className="strong">{shipmentOption.boxPackageIds.length}</span></span>}
          <span className="weak">Units: <span className="strong">{totalUnits}</span></span>
          <span className="weak">SKUs: <span className="strong">{totalSkus}</span></span>
          {spd && <span className="weak">Weight: <span className="strong">{totalWeight}lbs</span></span>}
        </div>
        <Button
          className={styles.viewContentsButton}
          type="secondary"
          size="small"
          onClick={() => setViewContentsModalOpen(true)}
        >
          View Contents
        </Button>
      </div>
    </>
  );
}
